import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PolicySeverity = 'info' | 'warning' | 'critical';
export type PolicyEnforcement = 'warn' | 'block';
export type MonitoringFrequency = 'day' | 'week';

export interface PolicyCheckConfig {
  enabled: boolean;
  threshold: number;
  severity: PolicySeverity;
  enforcement: PolicyEnforcement;
}

export interface PolicyCheckCardProps {
  title: string;
  config: PolicyCheckConfig;
  onChange: (config: PolicyCheckConfig) => void;
  monitoringFrequency: MonitoringFrequency;
  expectedHoursPerWeek: number;
  dailyHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  isMissingHours?: boolean;
  isNonWorkingDay?: boolean;
  isLegalMaximum?: boolean;
}

const getEnforcementBorderColor= (enforcement: PolicyEnforcement, enabled: boolean) => {
  if (!enabled) return 'border-l-muted-foreground/30';
  if (enforcement === 'block') return 'border-l-red-500 dark:border-l-red-400';
  return 'border-l-amber-400 dark:border-l-amber-500';
};

export const PolicyCheckCard: React.FC<PolicyCheckCardProps> = ({
  title,
  config,
  onChange,
  monitoringFrequency,
  expectedHoursPerWeek,
  dailyHours,
  isMissingHours = false,
  isNonWorkingDay = false,
  isLegalMaximum = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [thresholdInput, setThresholdInput] = React.useState<string>(config.threshold.toString());

  React.useEffect(() => {
    setThresholdInput(config.threshold.toString());
  }, [config.threshold]);

  const handleToggle = (checked: boolean) => {
    onChange({ ...config, enabled: checked });
  };

  const handleThresholdChange = (value: string) => {
    setThresholdInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange({ ...config, threshold: numValue });
    }
  };

  const handleThresholdBlur = () => {
    const numValue = parseFloat(thresholdInput);
    if (isNaN(numValue) || numValue < 0) {
      setThresholdInput(config.threshold.toString());
    }
  };

  const handleSeverityChange = (value: PolicySeverity) => {
    onChange({ ...config, severity: value });
  };

  const handleEnforcementChange = (value: PolicyEnforcement) => {
    onChange({ ...config, enforcement: value });
  };

  const weeklyAlertThreshold = isMissingHours
    ? expectedHoursPerWeek - config.threshold
    : expectedHoursPerWeek + config.threshold;

  const getDailyBreakdown = () => {
    if (!dailyHours) return null;
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
    return dayKeys.map((key, i) => {
      const hours = dailyHours[key];
      if (hours === 0) return null;
      const alertAt = isMissingHours ? hours - config.threshold : hours + config.threshold;
      return { name: dayNames[i], hours, alertAt };
    }).filter(Boolean) as { name: string; hours: number; alertAt: number }[];
  };

  const getSummaryText = () => {
    if (!config.enabled) return 'Disabled';
    if (isNonWorkingDay) return 'Alerts on non-working days';
    if (isLegalMaximum) {
      return `Triggers at >${config.threshold}h per ${monitoringFrequency}`;
    }
    if (monitoringFrequency === 'week') {
      return isMissingHours
        ? `Triggers at -${config.threshold}h under the working profile for the week`
        : `Triggers at +${config.threshold}h over the working profile for the week`;
    }
    return isMissingHours
      ? `Triggers at -${config.threshold}h under the working pattern for the day`
      : `Triggers at +${config.threshold}h over the working pattern for the day`;
  };

  const borderColor = getEnforcementBorderColor(config.enforcement, config.enabled);

  return (
    <TooltipProvider>
    <div
      className={cn(
        'border rounded-lg border-l-4 transition-all duration-200',
        borderColor,
        !config.enabled && 'opacity-60 bg-muted/20',
      )}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-accent/30 transition-colors rounded-t-lg"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsExpanded(!isExpanded); } }}
      >
        <div
          className="flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            aria-label={`Enable ${title}`}
            className="h-4 w-4 rounded border-gray-300"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('text-sm font-semibold', !config.enabled && 'text-muted-foreground')}>
              {title}
            </span>
            {!isExpanded && (
              <>
                <Separator orientation="vertical" className="h-4" />
                <span className="text-xs text-muted-foreground truncate">
                  {getSummaryText()}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!isExpanded && !config.enabled && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Off
            </Badge>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-180',
            )}
          />
        </div>
      </div>

      <div
        className={cn(
          'grid transition-all duration-200',
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-2 border-t space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  {isLegalMaximum
                    ? 'Maximum allowed'
                    : (isNonWorkingDay
                        ? 'Alert when'
                        : (monitoringFrequency === 'week' ? 'Expected hours per week' : 'Expected hours'))}
                </Label>
                {isLegalMaximum ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={thresholdInput}
                      onChange={(e) => handleThresholdChange(e.target.value)}
                      onBlur={handleThresholdBlur}
                      disabled={!config.enabled}
                      step="0.5"
                      min="0"
                      className="flex-1"
                    />
                    <span className="text-sm text-muted-foreground">
                      hours per {monitoringFrequency}
                    </span>
                  </div>
                ) : (
                  <Input
                    type="text"
                    value={
                      isNonWorkingDay
                        ? 'Time logged on weekends/holidays'
                        : (monitoringFrequency === 'week'
                            ? `${expectedHoursPerWeek}h (from working profile)`
                            : 'Per day (from working profile)')
                    }
                    disabled
                    className="bg-muted/50 cursor-not-allowed"
                  />
                )}
              </div>

              {!isNonWorkingDay && !isLegalMaximum ? (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">
                    {isMissingHours ? 'Alert when under by' : 'Alert when over by'}
                  </Label>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={thresholdInput}
                        onChange={(e) => handleThresholdChange(e.target.value)}
                        onBlur={handleThresholdBlur}
                        disabled={!config.enabled}
                        step="0.5"
                        min="0"
                        className="flex-1"
                      />
                      <span className="text-sm text-muted-foreground">hour(s)</span>
                    </div>
                    {monitoringFrequency === 'week' && (
                      <p className="text-xs text-muted-foreground">
                        will alert at {weeklyAlertThreshold.toFixed(1)}h
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div />
              )}
            </div>

            {monitoringFrequency === 'day' && !isNonWorkingDay && !isLegalMaximum && dailyHours && (
              <div className="text-xs text-muted-foreground space-y-1 p-2 bg-muted/30 rounded">
                <p className="font-medium">Daily alert thresholds:</p>
                {getDailyBreakdown()?.map((day) => (
                  <p key={day.name}>{day.name} ({day.hours}h): alerts at {day.alertAt.toFixed(1)}h</p>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm text-muted-foreground">Severity</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px]">
                      <p>{isLegalMaximum
                        ? 'Legal maximum violations are always Critical. They appear first in Insights with red styling to highlight regulatory compliance issues.'
                        : 'Controls the visual priority of this alert in Insights. Critical alerts appear first and use red styling. Info alerts appear last and can be hidden via display filters.'
                      }</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {isLegalMaximum ? (
                  <Input
                    type="text"
                    value="Critical (fixed)"
                    disabled
                    className="bg-muted/50 cursor-not-allowed"
                  />
                ) : (
                  <select
                    value={config.severity}
                    onChange={(e) => handleSeverityChange(e.target.value as PolicySeverity)}
                    disabled={!config.enabled}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="critical">Critical</option>
                  </select>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm text-muted-foreground">Enforcement</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px]">
                      <p>{isLegalMaximum
                        ? 'Legal maximum violations always block timesheet submission. The timesheet cannot be submitted until the violation is resolved.'
                        : 'Warn only shows the alert but allows submission. Block prevents timesheet submission until the violation is resolved.'
                      }</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                {isLegalMaximum ? (
                  <Input
                    type="text"
                    value="Block submission (fixed)"
                    disabled
                    className="bg-muted/50 cursor-not-allowed"
                  />
                ) : (
                  <select
                    value={config.enforcement}
                    onChange={(e) => handleEnforcementChange(e.target.value as PolicyEnforcement)}
                    disabled={!config.enabled}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="warn">Warn only</option>
                    <option value="block">Block submission</option>
                  </select>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};
